import { useState } from 'react'
import {
  Globe,
  Heart,
  Sparkles,
  MessageSquare,
  FileText,
  Download,
  ChevronRight,
  ArrowRight,
  Zap,
  Star,
  Target,
  Info,
  Clock,
  Bell,
  Scale
} from 'lucide-react'
import './PropositoEmAcaoPage.css'

function PropositoEmAcaoPage() {
  const [activeTab, setActiveTab] = useState('all')

  const announcements = [
    {
      id: '1',
      title: 'Expansão CD Logístico Sul',
      category: 'Corporativo',
      date: 'Há 2 dias',
      priority: 'high',
      content: 'Iniciamos as operações do nosso novo centro de distribuição em Porto Alegre para agilizar as entregas na região Sul.'
    },
    {
      id: '2',
      title: 'Nova Política de Home Office',
      category: 'RH',
      date: 'Há 5 dias',
      priority: 'medium',
      content: 'Atualizamos nossa política de trabalho híbrido para garantir mais flexibilidade e produtividade.'
    },
    {
      id: '3',
      title: 'Treinamento Littmann Gold',
      category: 'Academy',
      date: 'Há 1 semana',
      priority: 'low',
      content: 'Certificação técnica obrigatória para consultores B2B disponível no portal Academy.'
    }
  ]

  const values = [
    {
      title: 'Inovação com Propósito',
      desc: 'Não buscamos a tecnologia pela tecnologia, mas pelo impacto que ela gera na vida dos pacientes.',
      icon: Zap,
      colorClass: 'value-icon-amber',
      bgClass: 'value-bg-amber'
    },
    {
      title: 'Cuidado em cada Detalhe',
      desc: 'Desde a embalagem até o suporte técnico, o cuidado é nossa assinatura.',
      icon: Heart,
      colorClass: 'value-icon-rose',
      bgClass: 'value-bg-rose'
    },
    {
      title: 'Agilidade Decisiva',
      desc: 'No setor médico, minutos salvam vidas. Nossa logística reflete essa urgência.',
      icon: Sparkles,
      colorClass: 'value-icon-primary',
      bgClass: 'value-bg-primary'
    }
  ]

  const policies = [
    { id: 'p1', name: 'Código de Ética e Conduta 2024', size: '2.4 MB', format: 'PDF' },
    { id: 'p2', name: 'Guia de Benefícios Medik Platinum', size: '1.8 MB', format: 'PDF' },
    { id: 'p3', name: 'Manual de Segurança Cibernética', size: '3.1 MB', format: 'PDF' },
    { id: 'p4', name: 'Política de Compliance Hospitalar', size: '1.2 MB', format: 'PDF' }
  ]

  const tabs = [
    { id: 'all', label: 'Tudo de Novo', icon: Bell },
    { id: 'culture', label: 'Nossa Cultura', icon: Heart },
    { id: 'policies', label: 'Políticas & Guias', icon: Scale }
  ]

  return (
    <div className="proposito-em-acao-page">
      <div className="proposito-container">
        {/* Hero Section */}
        <div className="proposito-hero">
          <div className="proposito-hero-overlay"></div>
          <div className="proposito-hero-content">
            <div className="proposito-badge">
              <Globe size={12} />
              <span>Propósito em ação</span>
            </div>
            <h1 className="proposito-title">
              Cultura, Conexão e <span className="proposito-title-accent">Excelência.</span>
            </h1>
            <p className="proposito-subtitle">
              Este é o espaço onde unimos nossa tecnologia ao nosso propósito humano. Encontre aqui as diretrizes que fazem da Pró Médico a líder em soluções para a saúde.
            </p>
          </div>
        </div>

        <div className="proposito-grid">
          {/* Sidebar Navigation */}
          <aside className="proposito-sidebar">
            <div className="proposito-sidebar-card">
              <h3 className="proposito-sidebar-title">Navegação Interna</h3>
              <div className="proposito-tabs">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`proposito-tab ${activeTab === tab.id ? 'proposito-tab-active' : ''}`}
                  >
                    <div className="proposito-tab-content">
                      <tab.icon size={18} />
                      <span>{tab.label}</span>
                    </div>
                    <ChevronRight
                      size={14}
                      className={activeTab === tab.id ? 'proposito-tab-chevron-visible' : 'proposito-tab-chevron-hidden'}
                    />
                  </button>
                ))}
              </div>

              <div className="proposito-sidebar-footer">
                <div className="proposito-channels">
                  <div className="proposito-channel-icon">
                    <MessageSquare size={18} />
                  </div>
                  <div>
                    <p className="proposito-channel-title">Canais Oficiais</p>
                    <p className="proposito-channel-subtitle">Suporte e Ouvidoria</p>
                  </div>
                </div>
                <button className="proposito-ethics-button">
                  Contatar Ética Pró Médico
                </button>
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="proposito-main">
            {/* Culture Section */}
            {(activeTab === 'all' || activeTab === 'culture') && (
              <section className="proposito-section">
                <div className="proposito-section-header">
                  <h2 className="proposito-section-title">
                    <Target size={24} className="proposito-section-icon" />
                    Fundamentos Medik
                  </h2>
                  <span className="proposito-section-label">Missão & Valores</span>
                </div>

                <div className="proposito-values-grid">
                  {values.map((val, i) => (
                    <div key={i} className="proposito-value-card">
                      <div className={`proposito-value-icon-container ${val.bgClass} ${val.colorClass}`}>
                        <val.icon size={28} />
                      </div>
                      <h4 className="proposito-value-title">{val.title}</h4>
                      <p className="proposito-value-desc">{val.desc}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Announcements Section */}
            {(activeTab === 'all' || activeTab === 'policies') && (
              <section className="proposito-section">
                <div className="proposito-section-header">
                  <h2 className="proposito-section-title">
                    <Bell size={24} className="proposito-section-icon" />
                    Mural de Comunicados
                  </h2>
                  <button className="proposito-link-button">Ver Histórico</button>
                </div>

                <div className="proposito-announcements">
                  {announcements.map((msg) => (
                    <div key={msg.id} className={`proposito-announcement ${msg.priority === 'high' ? 'proposito-announcement-high' : ''}`}>
                      {msg.priority === 'high' && (
                        <div className="proposito-announcement-priority-bar"></div>
                      )}
                      <div className="proposito-announcement-icon">
                        <MessageSquare size={24} />
                      </div>
                      <div className="proposito-announcement-content">
                        <div className="proposito-announcement-header">
                          <div className="proposito-announcement-meta">
                            <span className="proposito-announcement-category">{msg.category}</span>
                            <span className="proposito-announcement-date">
                              <Clock size={10} />
                              {msg.date}
                            </span>
                          </div>
                          {msg.priority === 'high' && (
                            <span className="proposito-announcement-priority">
                              <Info size={10} />
                              Prioritário
                            </span>
                          )}
                        </div>
                        <h3 className="proposito-announcement-title">{msg.title}</h3>
                        <p className="proposito-announcement-text">{msg.content}</p>
                        <div className="proposito-announcement-action">
                          <button className="proposito-read-more">
                            Ler Comunicado Completo
                            <ArrowRight size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Policies List */}
            {(activeTab === 'all' || activeTab === 'policies') && (
              <section className="proposito-section">
                <div className="proposito-section-header">
                  <h2 className="proposito-section-title">
                    <FileText size={24} className="proposito-section-icon" />
                    Diretrizes & Compliance
                  </h2>
                </div>

                <div className="proposito-policies">
                  {policies.map((pol) => (
                    <div key={pol.id} className="proposito-policy-item">
                      <div className="proposito-policy-info">
                        <div className="proposito-policy-icon-container">
                          <FileText size={20} />
                        </div>
                        <div>
                          <p className="proposito-policy-name">{pol.name}</p>
                          <div className="proposito-policy-meta">
                            <span>{pol.format}</span>
                            <span>•</span>
                            <span>{pol.size}</span>
                          </div>
                        </div>
                      </div>
                      <button className="proposito-policy-download">
                        <Download size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Special Quote Section */}
            <div className="proposito-quote">
              <div className="proposito-quote-overlay"></div>
              <div className="proposito-quote-glow"></div>
              <div className="proposito-quote-content">
                <Star className="proposito-quote-star" size={32} />
                <h3 className="proposito-quote-text">
                  "Excelência não é um ato, mas um hábito. Na Pró Médico, nosso hábito é salvar vidas com precisão."
                </h3>
                <div className="proposito-quote-divider"></div>
                <p className="proposito-quote-author">Manifesto Medik v4.2</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PropositoEmAcaoPage
